import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Typography, Paper, Divider, CircularProgress, Button } from '@material-ui/core';
import { TextField, SmSpace, MdSpace } from 'm2m.components';
import { DataGridPro, GridColumns, GridRowsProp, DataGridProProps } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { FuncaoParticipante, InstrumentoFinanceiroApiResponse, ParticipanteResponse, RegistroStatus } from 'common/genericTypes';
import { addCpfCnpjMask } from 'utils/mask';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';

interface InvestidorData {
    id: string;
    hierarchy: string[];
    tipo: string;
    razaoSocial: string;
    cpfCnpj: string;
    quantidade: number;
    label: string;
}

interface CoordenadorData {
    id: string;
    hierarchy: string[];
    nome: string;
    cnpj: string;
    limite: number;
    distribuido: number;
    livre: number;
}

type MapaAlocacaoRow = CoordenadorData | InvestidorData;

const MapaAlocacao: React.FC = () => {
    const dispatch = useDispatch();
    const usuario = useSelector((state: RootState) => state.auth.usuario);
    const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingTreeData, setLoadingTreeData] = useState<boolean>(false);
    const [data, setData] = useState<GridRowsProp>([]);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [rowCount, setRowCount] = useState<number>(0);
    const [participantes, setParticipantes] = useState<Array<ParticipanteResponse>>([]);
    const [emissor, setEmissor] = useState<ParticipanteResponse | null>(null);

    const cnaeEspecificos = [
        '6422100', '6423900', '6424700', '6425501', '6429999', '6430100',
        '6462000', '6463800', '6612601', '6612602', '6612603', '6630200',
        '6499999', '6491300', '6499901', '6499902', '6511100', '6512000',
        '6513800', '6621501', '6530800', '6530801'
    ];

    const extrairDescricaoCnae = (cnaePrincipal: string): string | null => {
        if (!cnaePrincipal) return null;
        
        const regex = /^(\d{7})\s*-\s*(.+)$/;
        const match = cnaePrincipal.match(regex);

        if (match) {
            const [, cnae, descricaoDetalhada] = match;

            if (cnaeEspecificos.includes(cnae)) {
                return descricaoDetalhada.trim();
            }
        }

        return null;
    };

    interface FiltrosState {
        instrumento: string;
        cnpjEmissor: string;
        dataEmissao: string;
    }

    const [filtros, setFiltros] = useState<FiltrosState>({
        instrumento: '',
        cnpjEmissor: '',
        dataEmissao: ''
    });

    const [appliedFiltros, setAppliedFiltros] = useState<FiltrosState>({
        instrumento: '',
        cnpjEmissor: '',
        dataEmissao: ''
    });

    const postInstrumentosCoordenadoresApi = useSelector((state: RootState) => state.coordenadoresApi.coordenadores.postInstrumentosCoordenadores);
    const postExportarMapaAlocacaoApi = useSelector((state: RootState) => state.coordenadoresApi.coordenadores.postExportarMapaAlocacao);
    const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantes);

    const instrumentoAdapt = useCallback((instrumentoData: any): MapaAlocacaoRow[] => {
        if (!instrumentoData) return [];

        const rows: MapaAlocacaoRow[] = [];

        const instrumentoId = instrumentoData.id?.toString() || `inst-${Math.random().toString(36).substring(2, 11)}`;
        const instrumentoCodigo = instrumentoData.codigo || `Instrumento-${instrumentoId}`;

        let distribuido = 0;

        if (instrumentoData?.custodiantes && Array.isArray(instrumentoData.custodiantes)) {
            instrumentoData.custodiantes.forEach((custodiano: any) => {
                if (custodiano?.investidoresPrivados && Array.isArray(custodiano.investidoresPrivados)) {
                    custodiano.investidoresPrivados.forEach((investidor: any) => {
                        distribuido += (investidor?.quantidade || 0);
                    });
                }
            });
        }

        const livre = instrumentoData?.quantidadeEmitida - distribuido;
        const instrumentoNode: any = {
            id: instrumentoId,
            hierarchy: [instrumentoCodigo],
            codigo: instrumentoCodigo,
            criacao: instrumentoData.status || 'Ativo',
            emissorCNPJ: instrumentoData.emissor?.cnpj || '',
            situacaoDistribuicao: instrumentoData.statusEmissao,
            quantidadeEmitida: instrumentoData?.quantidadeEmitida || 0,
            distribuido: distribuido,
            livre: livre
        };

        rows.push(instrumentoNode);

        if (instrumentoData.coordenadores && Array.isArray(instrumentoData.coordenadores) && instrumentoData.coordenadores.length > 0) {
            instrumentoData.coordenadores.forEach((coordenador: any, coordIndex: number) => {
                if (!coordenador) return;

                const coordId = `${instrumentoId}-coord-${coordIndex + 1}`;
                const coordNome = coordenador.razaoSocial || `Coordenador ${coordIndex + 1}`;

                const limite = coordenador.valor || 0;
                const distribuido = 0;
                const livre = limite - distribuido;

                const coordenadorNode: CoordenadorData = {
                    id: coordId,
                    hierarchy: [instrumentoCodigo, coordNome],
                    nome: coordNome,
                    cnpj: coordenador.cnpj || '',
                    limite: limite,
                    distribuido: distribuido,
                    livre: livre < 0 ? 0 : livre
                };

                rows.push(coordenadorNode);

                const custodianteRelacionado = instrumentoData.custodiantes?.find(
                    (custodiante: any) => custodiante && custodiante.custodianteId === coordenador.id
                );

                if (custodianteRelacionado &&
                    custodianteRelacionado.investidoresPrivados &&
                    Array.isArray(custodianteRelacionado.investidoresPrivados) &&
                    custodianteRelacionado.investidoresPrivados.length > 0) {

                    let totalDistribuido = 0;

                    const investidoresPorTipo: { [tipo: string]: { quantidade: number, count: number } } = {};

                    custodianteRelacionado.investidoresPrivados.forEach((investidor: any) => {
                        if (!investidor) return;

                        let tipoInvestidor = 'PF';
                        
                        if (investidor.cpfCnpj && investidor.cpfCnpj.length > 11) {
                            const descricaoCnae = extrairDescricaoCnae(investidor.cnaePrincipal);
                            tipoInvestidor = descricaoCnae || 'PJ';
                        }
                        
                        let quantidade = 0;
                        if (typeof investidor.quantidade === 'object' && investidor.quantidade !== null) {
                            quantidade = investidor.quantidade.parsedValue || 0;
                        } else {
                            quantidade = investidor.quantidade || 0;
                        }

                        totalDistribuido += quantidade;

                        if (!investidoresPorTipo[tipoInvestidor]) {
                            investidoresPorTipo[tipoInvestidor] = { quantidade: 0, count: 0 };
                        }

                        investidoresPorTipo[tipoInvestidor].quantidade += quantidade;
                        investidoresPorTipo[tipoInvestidor].count += 1;
                    });

                    Object.entries(investidoresPorTipo).forEach(([tipo, dados]) => {
                        const uniqueId = `${tipo.replace(/[^a-zA-Z0-9]/g, '_')}_${coordId}_${Math.random().toString(36).substring(2, 9)}`;
                        
                        const investidorNode: InvestidorData = {
                            id: uniqueId,
                            hierarchy: [instrumentoCodigo, coordNome, tipo],
                            tipo: tipo,
                            razaoSocial: `${tipo} (${dados.count} investidor${dados.count > 1 ? 'es' : ''})`,
                            cpfCnpj: '',
                            quantidade: dados.quantidade,
                            label: tipo
                        };

                        rows.push(investidorNode);
                    });

                    const coordIndex = rows.findIndex(row => row.id === coordId);
                    if (coordIndex !== -1) {
                        const coordRow = rows[coordIndex] as CoordenadorData;
                        coordRow.distribuido = totalDistribuido;
                        coordRow.livre = (coordRow.limite - totalDistribuido) < 0 ? 0 : (coordRow.limite - totalDistribuido);
                    }
                }
            });
        }

        return rows;
    }, [usuario?.participante.id]);

    const rowGrouping = {
        headerName: 'Código do Ativo',
        minWidth: 200,
        valueFormatter: (params: any) => {
            if (params.id && typeof params.id === 'string') {
                const item = data.find((item) => item.id === params.id);

                if (item && 'label' in item) {
                    return item.label;
                }

                if (params.id.includes('_coord-') && params.id.includes('_')) {
                    if (params.id.startsWith('PF_') || params.id.startsWith('PJ_')) {
                        return params.id.substring(0, 2);
                    }
                    if (item && 'tipo' in item) {
                        return item.tipo;
                    }
                }
            }
            return params.value;
        }
    }

    const columns: GridColumns = [
        {
            field: 'emissorCNPJ',
            headerName: 'CNPJ do Emissor',
            minWidth: 150,
            flex: 1,
            valueFormatter: (params) => params.value ? addCpfCnpjMask(params.value) : ''
        },
        {
            field: 'quantidadeEmitida',
            headerName: 'Qtd. Emitida',
            minWidth: 120,
            type: 'number',
            valueFormatter: (params) => params.value ? params.value.toLocaleString() : ''
        },
        {
            field: 'distribuido',
            headerName: 'Qtd. Delimitada',
            minWidth: 120,
            type: 'number',
            valueFormatter: (params) => params.value ? params.value.toLocaleString() : ''
        },
        {
            field: 'quantidade',
            headerName: 'Qtd. Distribuída',
            minWidth: 120,
            type: 'number',
            valueFormatter: (params) => params.value ? params.value.toLocaleString() : ''
        },
        {
            field: 'livre',
            headerName: 'Saldo',
            minWidth: 90,
            type: 'number',
            valueFormatter: (params) => {
                const value = params.value < 0 ? 0 : params.value;
                return value ? value.toLocaleString() : '';
            }
        },
    ];

    const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) => {
        if (!row.hierarchy || !Array.isArray(row.hierarchy) || row.hierarchy.length === 0) {
            return [row.id.toString()];
        }

        if (row.hierarchy.length === 3) {
            if ('tipo' in row && row.tipo) {
                return [
                    row.hierarchy[0],
                    row.hierarchy[1],
                    row.id.toString()
                ];
            }

            return [
                row.hierarchy[0],
                row.hierarchy[1],
                row.hierarchy[2]
            ];
        }

        return row.hierarchy.map((item: any) =>
            item ? item.toString() : `unknown-${Math.random().toString(36).substr(2, 9)}`
        );
    };

    const fetchInstrumentos = useCallback(async () => {
        try {
            setLoading(true);
            setLoadingTreeData(true);
            setData([]);

            const filtros: {
                coordenadorId: string;
                codigo?: string;
                emissorId?: string;
                page: number;
                pageSize: number;
            } = {
                coordenadorId: usuario?.participante.id,
                page: page,
                pageSize: pageSize
            };

            if (appliedFiltros.instrumento.trim() !== '') {
                filtros.codigo = appliedFiltros.instrumento.trim();
            }

            if (appliedFiltros.cnpjEmissor.trim() !== '') {
                filtros.emissorId = appliedFiltros.cnpjEmissor.trim();
            }

            const result = await postInstrumentosCoordenadoresApi(
                dispatch,
                filtros,
                true
            );

            if (result && result.data && result.pagination) {
                setInstrumentos(result.data);
                setRowCount(result.pagination.total);

                if (result.data && result.data.length > 0) {
                    const mapaAlocacao: MapaAlocacaoRow[] = [];

                    for (const instrumento of result.data) {
                        const instrumentoTreeData = instrumentoAdapt(instrumento);
                        if (instrumentoTreeData && instrumentoTreeData.length > 0) {
                            mapaAlocacao.push(...instrumentoTreeData);
                        }
                    }

                    setData(mapaAlocacao);
                } else {
                    setData([]);
                }
            } else {
                setData([]);
                setRowCount(0);
            }
        } catch (error) {
            setData([]);
            setRowCount(0);
        } finally {
            setLoading(false);
            setLoadingTreeData(false);
        }
    }, [dispatch, page, pageSize, instrumentoAdapt, usuario?.participante.id, postInstrumentosCoordenadoresApi, appliedFiltros]);

    useEffect(() => {
        fetchInstrumentos();
    }, [fetchInstrumentos, page, pageSize]);

    const handlePesquisar = () => {
        setPage(1);
        setAppliedFiltros(filtros);
        fetchInstrumentos();
    };

    const handleLimparFiltros = () => {
        const emptyFiltros = {
            instrumento: '',
            cnpjEmissor: '',
            dataEmissao: ''
        };
        setEmissor(null);
        setFiltros(emptyFiltros);
        setAppliedFiltros(emptyFiltros);
        setPage(1);
        fetchInstrumentos();
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage + 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const htmlToPDF = async (htmlContent: string, filename: string) => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Por favor, permita pop-ups para gerar o PDF');
            return;
        }

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        };
    };

    const handleExportarPDF = async () => {
        try {
            setLoading(true);
            const filtros: {
                coordenadorId: string;
                codigo?: string;
                emissorId?: string;
                page: number;
                pageSize: number;
            } = {
                coordenadorId: usuario?.participante.id,
                page: page,
                pageSize: pageSize
            };

            if (appliedFiltros.instrumento.trim() !== '') {
                filtros.codigo = appliedFiltros.instrumento.trim();
            }

            if (appliedFiltros.cnpjEmissor.trim() !== '') {
                filtros.emissorId = appliedFiltros.cnpjEmissor.trim();
            }

            const htmlContent = await postExportarMapaAlocacaoApi(dispatch, filtros, true);
            const filename = `mapa-alocacao-${new Date().toISOString().split('T')[0]}.pdf`;

            await htmlToPDF(htmlContent, filename);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            alert('Erro ao gerar PDF. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const getParticipantes = useCallback(async () => {
        try {
            const result = await getParticipantesApi(
                dispatch,
                {
                    funcoes: [FuncaoParticipante.Emissor],
                    status: RegistroStatus.Aprovado,
                    suspenso: false,
                },
                true);
            setParticipantes(result);
        } catch (error) { }
    }, [dispatch]);

    useEffect(() => {
        getParticipantes();
    }, [getParticipantes]);

    return (
        <div style={{ marginRight: 40, marginBottom: 40 }}>
            <Typography variant="h2">{'Painel de Alocação'}</Typography>
            <MdSpace />

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper style={{ padding: 24 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Código do Instrumento"
                                    value={filtros.instrumento}
                                    onChange={(event) => {
                                        const newValue = typeof event === 'object' && event.target ? event.target.value : event;
                                        setFiltros(prev => ({
                                            ...prev,
                                            instrumento: newValue || ''
                                        }));
                                    }}
                                    fullWidth
                                    placeholder="Pesquisar por código do instrumento"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <ParticipanteAutoComplete
                                    label={`Emissor`}
                                    participantes={participantes}
                                    participante={emissor}
                                    onChange={(value) => {
                                        if (value) {
                                            setEmissor(value);
                                            setFiltros(prev => ({
                                                ...prev,
                                                cnpjEmissor: value.id
                                            }));
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                                    <div style={{ display: 'flex', gap: '16px' }}>
                                        <Button
                                            variant="outlined"
                                            color="default"
                                            onClick={handleLimparFiltros}
                                            disabled={loading}
                                        >
                                            Limpar Filtros
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handlePesquisar}
                                            disabled={loading}
                                        >
                                            Pesquisar
                                        </Button>
                                    </div>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleExportarPDF}
                                        disabled={loading}
                                    >
                                        Exportar em PDF
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper style={{ padding: 24 }}>
                        <Grid item xs={12}>
                            <Divider style={{ margin: '16px 0' }} />
                        </Grid>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <div style={{
                                    height: 600,
                                    width: '100%',
                                    position: 'relative',
                                    overflow: 'auto'
                                }}>
                                    {loadingTreeData && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            zIndex: 2
                                        }}>
                                            <CircularProgress />
                                        </div>
                                    )}

                                    <DataGridPro
                                        treeData
                                        rows={data}
                                        columns={columns}
                                        getTreeDataPath={getTreeDataPath}
                                        disableSelectionOnClick
                                        density="standard"
                                        getRowClassName={(params) => {
                                            const row = params.row;

                                            if (row && row.hierarchy) {
                                                if (row.hierarchy.length === 1) {
                                                    return 'instrumento-row';
                                                } else if (row.hierarchy.length === 2) {
                                                    return 'coordenador-row';
                                                } else if (row.hierarchy.length === 3) {
                                                    return 'investidor-row';
                                                }
                                            }

                                            return '';
                                        }}
                                        groupingColDef={rowGrouping}
                                        pagination
                                        paginationMode="server"
                                        page={page - 1}
                                        pageSize={pageSize}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                        rowCount={rowCount}
                                        loading={loadingTreeData}
                                        sx={{
                                            '& .instrumento-row': {
                                                backgroundColor: '#f0f7ff',
                                                '&:hover': {
                                                    backgroundColor: '#e0f0ff',
                                                }
                                            },
                                            '& .coordenador-row': {
                                                backgroundColor: '#f0fff7',
                                                '&:hover': {
                                                    backgroundColor: '#e0fff0',
                                                }
                                            },
                                            '& .investidor-row': {
                                                backgroundColor: '#fff7f0',
                                                '&:hover': {
                                                    backgroundColor: '#fff0e0',
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </Grid>

                            <SmSpace />
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </div>
    );
};

export default MapaAlocacao;